import React, { useState, useEffect, useRef } from 'react';

// Icono X para cerrar
const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const EditPatientModal = ({
  isOpen,
  onClose,
  paciente,
  onUpdate,
  rut,
  rutError,
  handleRutChange,
  verificandoExistencia,
  pacienteExistente,
  rutOriginal,
  documentoId,
  country,
  handleDocumentoIdChange,
  documentoIdError,
  isUpdating
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    correo: '',
    enfermedad_base: '',
    contacto_emergencia: '',
    caso_clinico: '',
    direccion: '',
    fecha_nacimiento: ''
  });
  
  const modalRef = useRef(null);

  // Cargar datos del paciente cuando se abre el modal
  useEffect(() => {
    if (paciente && isOpen) {
      setFormData({
        nombre: paciente.nombre || '',
        telefono: paciente.telefono || '',
        correo: paciente.correo || '',
        enfermedad_base: paciente.enfermedad_base || '',
        contacto_emergencia: paciente.contacto_emergencia || '',
        caso_clinico: paciente.caso_clinico || '',
        direccion: paciente.direccion || '',
        fecha_nacimiento: paciente.fecha_nacimiento || ''
      });
    }
  }, [paciente, isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Cerrar al hacer click fuera del modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleOverlayClick}
    >
      {/* Modal Container */}
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn"
        style={{
          animation: 'scaleIn 0.2s ease-out'
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════
            HEADER
        ═══════════════════════════════════════════════════════════════ */}
        <div className="bg-[#F4E1D2]/30 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#2A9D8F]">Editar Paciente</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
            title="Cerrar"
          >
            <CloseIcon />
          </button>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            FORM BODY
        ═══════════════════════════════════════════════════════════════ */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            
            {/* ─────────── COLUMNA IZQUIERDA ─────────── */}
            
            {/* País de Residencia */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                País de Residencia
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="country" 
                    value="CL" 
                    checked={country === 'CL'} 
                    onChange={() => {}} 
                    className="text-[#2a9d8f] focus:ring-[#2a9d8f]" 
                  />
                  <span className="text-sm">Chile</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="country" 
                    value="MX" 
                    checked={country !== 'CL'} 
                    onChange={() => {}} 
                    className="text-[#2a9d8f] focus:ring-[#2a9d8f]" 
                  />
                  <span className="text-sm">Otro</span>
                </label>
              </div>
            </div>

            {/* Documento de Identidad */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                {country === 'CL' ? 'RUT / Documento' : 'Documento de Identidad / CURP'}
              </label>
              <div className="relative">
                {country === 'CL' ? (
                  <>
                    <input
                      type="text"
                      value={rut}
                      onChange={handleRutChange}
                      className={`w-full px-4 py-2.5 rounded-lg border shadow-sm transition-all ${
                        rutError 
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400' 
                          : pacienteExistente && rut !== rutOriginal
                            ? 'border-yellow-300 focus:ring-yellow-200 focus:border-yellow-400'
                            : 'border-gray-200 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F]'
                      } focus:ring-2 focus:outline-none`}
                      placeholder="12.345.678-9"
                    />
                    {verificandoExistencia && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#2A9D8F] border-t-transparent"></div>
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={documentoId}
                    onChange={handleDocumentoIdChange}
                    className={`w-full px-4 py-2.5 rounded-lg border shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all ${
                      documentoIdError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ingrese ID, DNI o CURP"
                    maxLength={20}
                  />
                )}
              </div>
              {country === 'CL' && rutError && <p className="mt-1 text-xs text-red-600">{rutError}</p>}
              {country === 'CL' && pacienteExistente && !rutError && rut !== rutOriginal && (
                <p className="mt-1 text-xs text-yellow-600">Este RUT ya está registrado</p>
              )}
              {country !== 'CL' && documentoIdError && <p className="mt-1 text-xs text-red-600">{documentoIdError}</p>}
            </div>

            {/* Nombre */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Nombre Completo
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
                required
              />
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Teléfono
              </label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
                required
              />
            </div>

            {/* Correo */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Correo Electrónico
              </label>
              <input
                type="email"
                value={formData.correo}
                onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
                required
              />
            </div>

            {/* Fecha Nacimiento */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Fecha de Nacimiento
              </label>
              <input
                type="date"
                value={formData.fecha_nacimiento}
                onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
              />
            </div>

            {/* Dirección */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Dirección
              </label>
              <input
                type="text"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
              />
            </div>

            {/* ─────────── FILA COMPLETA ─────────── */}

            {/* Enfermedad Base */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Enfermedad Base
              </label>
              <input
                type="text"
                value={formData.enfermedad_base}
                onChange={(e) => setFormData({ ...formData, enfermedad_base: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
              />
            </div>

            {/* Contacto de Emergencia */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Contacto de Emergencia
              </label>
              <input
                type="text"
                value={formData.contacto_emergencia}
                onChange={(e) => setFormData({ ...formData, contacto_emergencia: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all"
                required
              />
            </div>

            {/* Caso Clínico / Notas */}
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Caso Clínico / Notas
              </label>
              <textarea
                value={formData.caso_clinico}
                onChange={(e) => setFormData({ ...formData, caso_clinico: e.target.value })}
                rows="3"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] focus:outline-none transition-all resize-none"
                placeholder="Observaciones clínicas, alergias, tratamientos anteriores..."
              />
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              FOOTER
          ═══════════════════════════════════════════════════════════════ */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isUpdating}
              className="px-5 py-2.5 rounded-lg font-medium text-gray-500 hover:bg-gray-100 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                isUpdating || 
                verificandoExistencia || 
                (country === 'CL' && (rutError || (pacienteExistente && rut !== rutOriginal))) ||
                (country !== 'CL' && (documentoIdError || !documentoId))
              }
              className="px-6 py-2.5 rounded-lg font-medium bg-[#E76F51] hover:bg-[#d65f41] text-white shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              )}
              {isUpdating ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </form>
      </div>

      {/* Animaciones CSS */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EditPatientModal;
